import { t } from '@hh/global.js'

class HHBackInStock extends HTMLElement {
  constructor() {
    super()
    this.form = this.querySelector('form')
    if (!this.form) return
    this.form.addEventListener('submit', this.submitForm.bind(this))

    this.klaviyoCompanyId = this.form.querySelector(
      '[name="klaviyo-company-id"]'
    ).value
    this.klaviyoListId = this.form.querySelector(
      '[name="klaviyo-list-id"]'
    ).value

    this.submitButton = this.querySelector('[type="submit"]')
    this.infoLabel = this.querySelector('.js-klaviyo-bis-label')
    this.successBanner = this.querySelector('.js-success-banner')
    this.errorBanner = this.querySelector('.js-error-banner')
    this.subscribeFields = this.querySelector('.js-klaviyo-subscribe')

    this.consentInput = this.querySelector('input[name="consent"]')
    this.emailInput = this.querySelector('input[name="contact[email]"]')
    this.emailInput.addEventListener('input', this.handleShowSubmit.bind(this))

    this.bisButton = document.querySelector('.js-show-bis')
  }

  connectedCallback() {
    this.consentInput.addEventListener('input', (e) =>
      this.validateInput(this.consentInput, 'Consent')
    )

    this.bisButton?.addEventListener('showBis', this.handleBisButtonClick.bind(this))
  }

  disconnectedCallback() {
    this.consentInput.removeEventListener('input', (e) =>
      this.validateInput(this.consentInput, 'Consent')
    )

    this.bisButton?.removeEventListener('showBis', this.handleBisButtonClick.bind(this))
  }

  handleBisButtonClick(e) {
    e.preventDefault();
    this.hideBisButton()
    this.closest('.js-bis')?.classList.remove('hidden')
  }

  hideBisButton() {
    this.bisButton?.classList.add('hidden')
  }

  validateInput(element, field) {
    this.hasError = false
    this.querySelectorAll('.klaviyo-bis__input, .hh-radio-input').forEach(
      (input) => {
        if (input.classList.contains('error')) {
          this.hasError = true
        }
      }
    )

    if (!this.hasError) {
      this.hideStatus()
    }
  }

  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@_-]+$/
    return emailRegex.test(email)
  }

  submitForm(evt) {
    evt.preventDefault()
    this.hideStatus()

    this.validateInput(this.consentInput, 'Consent')

    this.hasError = false
    this.querySelectorAll('.klaviyo-bis__input, .hh-radio-input').forEach(
      (input) => {
        if (input.classList.contains('error')) {
          this.hasError = true
        }
      }
    )

    if (this.hasError) {
      this.showError()
      return
    }

    this.submitButton?.setAttribute('disabled', true)
    if (!this.klaviyoCompanyId || !this.klaviyoListId) {
      this.adminError()
    }

    const formData = new FormData(this.form)
    const email = formData.get('contact[email]')

    this.createBackInStockSubscription(email);
    if (formData.get('consent') === 'on') {
      this.createEmailSubscription(email)
    }
  }

  // Show the form using accordion animation
  handleShowSubmit(event) {
    if (this.validateEmail(event.target.value.trim())) {
      this.subscribeFields.style.opacity = 1
      this.subscribeFields.style.maxHeight =
        this.subscribeFields.scrollHeight + 'px'
    } else {
      this.subscribeFields.style.opacity = 0
      this.subscribeFields.style.maxHeight = 0
      this.hideStatus()
    }
  }

  async createBackInStockSubscription(email) {
    window.dataLayer = window.dataLayer || [];

    const variantId = document.querySelector('#product-form-installment input[name="id"]')?.value;
    if (!variantId) return;

    const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.klaviyoCompanyId}`
    const options = {
      method: 'POST',
      headers: {
        revision: '2023-12-15',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type:  'profile',
                attributes: {
                  email: email,
                },
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`
              },
            },
          },
        },
      }),
    }

    try {
      const res = await fetch(url, options)
      if (!res.ok) throw new Error('Klaviyo API error')

      this.submitButton.textContent = t('products.product.success')
      this.infoLabel.textContent = t('products.product.bis.form.register_notification_success')

      // Reset the status after 5 seconds
      setTimeout(() => {
        this.submitButton.textContent = t(
          'products.product.email_me_when_in_stock'
        )
        this.infoLabel.textContent = t('products.product.bis.form.register_notification')

        const emailInput = this.form.querySelector(
          'input[name="contact[email]"]'
        )
        emailInput.value = ''

        const inputWrapper = this.form.querySelector('hh-input-wrapper')
        inputWrapper.removeAttribute('filled')

        this.consentInput.checked = false

        this.subscribeFields.style.opacity = 0
        this.subscribeFields.style.maxHeight = 0
      }, 5000)
    } catch (error) {
      console.error(error)
      this.showError()
    } finally {
      this.submitButton?.removeAttribute('disabled')
    }
  }

  async createEmailSubscription(email) {
    window.dataLayer = window.dataLayer || []

    const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.klaviyoCompanyId}`
    const options = {
      method: 'POST',
      headers: {
        revision: '2023-12-15',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type:  'profile',
                attributes: {
                  email: email,
                },
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: this.klaviyoListId,
              },
            },
          },
        },
      }),
    }

    try {
      const res = await fetch(url, options)
      if (!res.ok) throw new Error('Klaviyo API error')
    } catch (error) {
      console.error(error)
    }
  }

  adminError() {
    console.error(
      'Klaviyo Company ID or List ID not set in theme editor settings'
    )
    this.submitButton.setAttribute('disabled', true)
    this.showError()
  }

  showError() {
    this.errorBanner?.removeAttribute('hidden')
  }

  hideStatus() {
    this.errorBanner?.setAttribute('hidden', true)
  }
}

customElements.define('hh-back-in-stock', HHBackInStock)
